import { useEffect, useState } from 'react';
import { collection, limit as fsLimit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { ConversationStatus } from '../lib/types';

export type StatusCounts = Record<ConversationStatus | 'all', number>;

export interface UnreadByStatus {
  /** Count of conversations with unread > 0, per status, plus `all` (the total). */
  counts: StatusCounts;
  /** Sum of unread message counts across all those conversations (favicon badge). */
  totalUnread: number;
}

const EMPTY: UnreadByStatus = {
  counts: { open: 0, pending: 0, snoozed: 0, resolved: 0, all: 0 },
  totalUnread: 0,
};

/**
 * Live per-status unread counters, independent of the InboxPage status/label
 * filter so every tab can show its badge at once. Subscribes once to
 * `unreadCount > 0` (the unread set is small) and buckets by status
 * client-side. Inbox-scope filter is applied client-side, mirroring
 * useTotalUnread, to avoid needing a composite index for inboxId `in` +
 * unreadCount range.
 */
export function useUnreadByStatus(inboxIds?: string[]): UnreadByStatus {
  const [state, setState] = useState<UnreadByStatus>(EMPTY);
  const inboxKey = (inboxIds ?? []).slice().sort().join(',');

  useEffect(() => {
    const q = query(collection(db, 'chats'), where('unreadCount', '>', 0), fsLimit(500));
    const allowed = inboxIds && inboxIds.length > 0 ? new Set(inboxIds) : null;
    const unsub = onSnapshot(
      q,
      (snap) => {
        const counts: StatusCounts = { open: 0, pending: 0, snoozed: 0, resolved: 0, all: 0 };
        let totalUnread = 0;
        for (const d of snap.docs) {
          const data = d.data() as { unreadCount?: number; inboxId?: string; status?: ConversationStatus };
          if (allowed && data.inboxId && !allowed.has(data.inboxId)) continue;
          const st = data.status;
          if (st && st in counts) counts[st] += 1;
          counts.all += 1;
          totalUnread += data.unreadCount ?? 0;
        }
        setState({ counts, totalUnread });
      },
      (err) => {
        console.error('useUnreadByStatus listener error', err);
      },
    );
    return unsub;
  }, [inboxKey]);

  return state;
}
